import { createContext } from 'preact'
import { useContext, useLayoutEffect } from 'preact/hooks'
import { assert } from '#shared'

/**
 * Screens opened on top of the current phase's screen, like Credits from the main menu.
 * UI-only navigation: the game phase doesn't change. The stack clears when the phase does.
 */
/** Screens that can be pushed; `screen-router.tsx` maps each to its component. */
export type StackScreen = 'credits' | 'settings' | 'newGame' | 'loadGame'

export interface ScreenStack {
  readonly push: (screen: StackScreen) => void
  /** Closes the top screen. */
  readonly back: () => void
  /** While set, back (Esc, B) calls `onBack` instead: a screen's own question, closed first. */
  readonly overrideBack: (onBack: (() => void) | null) => void
}

export const ScreenStackContext = createContext<ScreenStack | null>(null)

export function useScreenStack(): ScreenStack {
  const stack = useContext(ScreenStackContext)
  assert(stack, 'useScreenStack() needs the <ScreenRouter> above it')
  return stack
}

/** While `onBack` is set, back calls it instead of closing the screen (a confirm step). */
export function useBackOverride(onBack: (() => void) | null): void {
  const { overrideBack } = useScreenStack()
  // A layout effect, so a back pressed right after the question appears already reaches it.
  useLayoutEffect(() => {
    overrideBack(onBack)
    return () => {
      overrideBack(null)
    }
  }, [overrideBack, onBack])
}
